import PropTypes from 'prop-types';
import cls from 'classnames';
import { useEffect, useRef, useState } from 'react';

// hooks
import useWindowSize from '@/utils/hooks/useWindowSize';
// helpers
import { getContent } from '@/utils/helper';
import { analytics, getHeroCtaDetail } from '@/utils/analytics';
// ui
import WestholmeLogo from '@/atoms/Logo/WestholmeWithLineLogo';
import Icon from '@/atoms/Icon/Icon';
import { DynamicAsset } from '@/molecules/Media/index';
import Navigation from '@/organisms/Navigation/Navigation';
import RichText from '@/molecules/RichText/RichText';

const X_PADDING = 80;
const MIN_LOGO_WIDTH = 107;
const MIN_LOGO_TOP = 32;
const WRAPPER_TOP = 112;

function HeaderHome({ assets = [], entries = [], fields = null, slug }) {
  const headerRef = useRef();
  const [hasBackground, setHasBackground] = useState(true);
  const [homeVideoAsset, setHomeVideoAsset] = useState(null);
  const windowSize = useWindowSize();

  const { text, subText, asset, title, navigation, callToAction } = fields;

  const topNavigationContent = navigation
    ? getContent(navigation.sys.id, entries)?.fields
    : null;

  if (callToAction) {
    callToAction.fields = getContent(callToAction.sys.id, entries).fields;
  }

  const maxWidth = +windowSize?.windowWidth - X_PADDING;
  const windowHeight = +windowSize?.windowHeight;

  const videoCaptions = [fields.textVideo1, fields.textVideo2, fields.textVideo3, fields.textVideo4, fields.textVideo5, fields.textVideo6, fields.textVideo7]

  useEffect(() => {
    const newCloudinaryAssets = fields.cloudinaryAssets.map((item, index) => {
      return {
        ...item,
        video_number: index
      }
    });

    const videoAssets = newCloudinaryAssets[Math.floor(Math.random() * newCloudinaryAssets.length)];
    setHomeVideoAsset(videoAssets);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const WHLogo = headerRef?.current?.querySelector(
        '.aaco-header__moving-logo > svg'
      );
      const WHLogoWrapper = headerRef?.current?.querySelector(
        '.aaco-header__moving-logo'
      );

      if (!WHLogo) return;
      // scroll up
      const hasOverflow = window.pageYOffset > 0;
      if (!hasOverflow) {
        WHLogo?.setAttribute('width', maxWidth);
        if (WHLogoWrapper) WHLogoWrapper.style.top = `${WRAPPER_TOP}px`;
      }

      // update width & top
      const WIDTH_SCALE =
        window.pageYOffset === 0 ? 1 : 1 - window.pageYOffset / windowHeight;

      let newWidth = +maxWidth * WIDTH_SCALE;

      let newTop =
        `${WHLogoWrapper?.style?.top}`?.replace('px', '') * WIDTH_SCALE;

      if (newWidth <= MIN_LOGO_WIDTH) {
        newWidth = MIN_LOGO_WIDTH;
        newTop = MIN_LOGO_TOP;
      }
      if (newTop <= MIN_LOGO_TOP) {
        newTop = MIN_LOGO_TOP;
      }

      if (newWidth !== WHLogo?.width?.baseVal?.value) {
        WHLogo?.setAttribute('width', newWidth);
        WHLogoWrapper.style.top = `${newTop}px`;
      }

      // has not finished scroll yet
      const hasFinishScroll =
        +window.pageYOffset + MIN_LOGO_TOP >= windowHeight;
      const hasBackgroundCurrent = !hasFinishScroll;
      setHasBackground(hasBackgroundCurrent);
    };
    handleScroll();

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [headerRef, windowSize?.windowWidth]);

  if (
    !windowSize?.windowHeight ||
    !windowSize?.windowWidth ||
    isNaN(windowSize?.windowWidth)
  )
  return (
    <div className="aaco-header w-scren h-screen mb-10 animate-pulse bg-gray-600 dark:bg-gray-700"></div>
  );


  return (
    <div
      className={cls(
        'aaco-header aaco-header-home aaco-header-wrapper',
        'relative',
        {
          'h-screen': [homeVideoAsset]|| asset,
        },
      )}
      aria-label={title}
      ref={headerRef}
    >
      <Navigation
        assets={assets}
        hasBackground={hasBackground}
        hideLogo={hasBackground}
        fields={topNavigationContent}
        entries={entries}
        slug={slug}
        navigationColor="transparent"
      />
      <div className="aaco-header__image absolute top-0 right-0  h-full w-full">
        <DynamicAsset
          field={[homeVideoAsset] ?? asset}
          assets={assets}
          entries={entries}
          container="full"
          title={[homeVideoAsset] && title}
        />
      </div>

      <div className="aaco-header__bg-shadow absolute top-0 right-0 bottom-0 left-0 h-full w-full" />
      <div className="aaco-header__bg-shadow__inset absolute right-0 bottom-0 left-0 " />
      <div
        className={`aaco-header__content leading-[1rem] absolute container left-[2%] mx-[30px] md:ml-10 border-l-1 border-white pl-4  bottom-[5%] w-full md:w-[400px] p-0
        }`}
      >
        {homeVideoAsset !== null && videoCaptions[homeVideoAsset.video_number].content.map(({ content, nodeType }, idx) => {
          return (
            <RichText
              entries={entries}
              content={content}
              key={idx}
              nodeType={nodeType}
              richTextStyle={{
                paragraph: {
                  customFont: ' text-caption uppercase !font-pro-mono text-white',
                },
              }}
            />
          );
        })}
      </div>

      {/* START: moving logo */}
      <div
        className={cls(
          'aaco-header__moving-logo',
          'w-screen xl:w-[1320px] 2xl:w-screen h-auto flex justify-center',
          'fixed top-[112px] xl:left-[4.5%] 2xl:left-0 2xl:px-[22.5%] z-20',
          `${!hasBackground && 'hidden'}`,
        )}
      >
        <WestholmeLogo color="#ffffff" width={maxWidth} height="auto" />
      </div>

      {/* END: moving logo */}
    </div>
  );
}

HeaderHome.propTypes = {
  assets: PropTypes.array,
  entries: PropTypes.array,
  fields: PropTypes.object,
};

export default HeaderHome;
